import { useState, useEffect, useCallback } from 'react';
import { DECK_INFO, shuffleArray } from '../mock/gameData';
import gameSounds from '../utils/gameSounds';

export const JOURNEY_STATES = {
  INTRO: 'intro',
  PLAYING: 'playing',
  DECK_COMPLETE: 'deck_complete',
  GAME_OVER: 'game_over',
  JOURNEY_COMPLETE: 'journey_complete'
};

const POINTS_TO_ADVANCE = 10;
const MAX_STRIKES = 3;

// Celebration messages that escalate
const CELEBRATIONS = [
  { title: "Nice Start!", subtitle: "You're on your way", emoji: "👍" },
  { title: "Keep Going!", subtitle: "Building momentum", emoji: "🔥" },
  { title: "On Fire!", subtitle: "You can't be stopped", emoji: "🔥🔥" },
  { title: "Incredible!", subtitle: "A true contender", emoji: "⚡" },
  { title: "Unstoppable!", subtitle: "The crowd goes wild", emoji: "🌟" },
  { title: "LEGENDARY!", subtitle: "History in the making", emoji: "👑" },
  { title: "GODLIKE!", subtitle: "Mere mortals tremble", emoji: "⚡👑⚡" },
  { title: "TRANSCENDENT!", subtitle: "Beyond comprehension", emoji: "🌌" },
  { title: "MYTHICAL!", subtitle: "Songs will be sung", emoji: "🏆🔥🏆" },
  { title: "ETERNAL!", subtitle: "Your name echoes through time", emoji: "✨🌟✨" },
];

// Get celebration based on decks passed (escalates every deck, caps at max)
const getCelebration = (decksCompleted) => {
  const index = Math.min(decksCompleted - 1, CELEBRATIONS.length - 1);
  return CELEBRATIONS[index];
};

export const useJourneyGame = ({ user }) => {
  const [gameState, setGameState] = useState(JOURNEY_STATES.INTRO);
  const [availableDecks, setAvailableDecks] = useState([]);
  const [currentDeckIndex, setCurrentDeckIndex] = useState(0);
  const [currentDeck, setCurrentDeck] = useState(null);
  const [decksCompleted, setDecksCompleted] = useState(0);
  const [score, setScore] = useState(0);
  const [strikes, setStrikes] = useState(0);
  const [currentCards, setCurrentCards] = useState([]);
  const [isRevealing, setIsRevealing] = useState(false);
  const [lastChoice, setLastChoice] = useState(null);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [nextDeckInfo, setNextDeckInfo] = useState(null);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [xRayVision, setXRayVision] = useState(false);
  const [pendingXRay, setPendingXRay] = useState(false);
  const [animatingCard, setAnimatingCard] = useState(null);
  const [cardResult, setCardResult] = useState(null);

  // Get random cards from a deck - defined first so it can be used below
  const getRandomCards = useCallback((deckType) => {
    const deckInfo = DECK_INFO[deckType];
    if (!deckInfo || !deckInfo.cards || deckInfo.cards.length < 2) {
      console.warn('Invalid deck or not enough cards for:', deckType);
      return [];
    }
    
    const shuffled = shuffleArray([...deckInfo.cards]);
    return shuffled.slice(0, 2);
  }, []);

  // Find a valid deck with cards
  const findValidDeck = useCallback((decks, startIndex) => {
    for (let i = startIndex; i < decks.length; i++) {
      const deckType = decks[i];
      const deckInfo = DECK_INFO[deckType];
      if (deckInfo && deckInfo.cards && deckInfo.cards.length >= 2) {
        return { index: i, deckType };
      }
    }
    return null;
  }, []);

  // Track deck play for "Recently Played" section
  const trackDeckPlay = useCallback(async (deckType) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const deckInfo = DECK_INFO[deckType];
      await fetch(`${backendUrl}/api/track-play`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          deck_type: deckType,
          deck_name: deckInfo?.name || deckType
        })
      });
    } catch (error) {
      console.error('Failed to track deck play:', error);
    }
  }, []);

  // Initialize the journey with shuffled decks
  const startJourney = useCallback(() => {
    const allDeckTypes = Object.keys(DECK_INFO);
    const shuffledDecks = shuffleArray([...allDeckTypes]);
    
    // Find first valid deck
    const validDeck = findValidDeck(shuffledDecks, 0);
    if (!validDeck) {
      console.error('No valid decks found!');
      return;
    }
    
    setAvailableDecks(shuffledDecks);
    setCurrentDeckIndex(validDeck.index);
    setDecksCompleted(0);
    setScore(0);
    setStrikes(0);
    setScoreSubmitted(false);
    setConsecutiveCorrect(0);
    setXRayVision(false);
    setPendingXRay(false);
    setAnimatingCard(null);
    setCardResult(null);
    
    // Set first deck
    setCurrentDeck(validDeck.deckType);
    setCurrentCards(getRandomCards(validDeck.deckType));
    setGameState(JOURNEY_STATES.PLAYING);
    
    // Track first deck play
    trackDeckPlay(validDeck.deckType);
  }, [getRandomCards, findValidDeck, trackDeckPlay]);

  // Advance to next deck
  const advanceToNextDeck = useCallback(() => {
    // Find next valid deck
    const validDeck = findValidDeck(availableDecks, currentDeckIndex + 1);
    
    // Check if journey is complete (no more valid decks)
    if (!validDeck) {
      setGameState(JOURNEY_STATES.JOURNEY_COMPLETE);
      return;
    }
    
    setCurrentDeckIndex(validDeck.index);
    setCurrentDeck(validDeck.deckType);
    setScore(0);
    setStrikes(0); // Reset strikes for new deck
    // Keep consecutiveCorrect streak across decks!
    setCurrentCards(getRandomCards(validDeck.deckType));
    setAnimatingCard(null);
    setCardResult(null);
    
    // Activate X-Ray if pending from previous deck
    if (pendingXRay) {
      setXRayVision(true);
      setPendingXRay(false);
    }
    
    setGameState(JOURNEY_STATES.PLAYING);
    
    // Track new deck play
    trackDeckPlay(validDeck.deckType);
  }, [currentDeckIndex, availableDecks, getRandomCards, findValidDeck, pendingXRay, trackDeckPlay]);

  // Handle card choice
  const handleChoice = useCallback((chosenCard, otherCard) => {
    if (isRevealing) return;
    
    setIsRevealing(true);
    setAnimatingCard(chosenCard.id);
    const isCorrect = chosenCard.rank < otherCard.rank;
    setCardResult(isCorrect ? 'winner' : 'loser');
    
    setLastChoice({
      chosen: chosenCard,
      other: otherCard,
      isCorrect
    });

    if (isCorrect) {
      gameSounds.playCorrect();
      const newScore = score + 1;
      setScore(newScore);
      
      // Track consecutive correct for X-Ray Vision
      const newConsecutive = consecutiveCorrect + 1;
      setConsecutiveCorrect(newConsecutive);
      
      // Award X-Ray Vision at every 4 in a row (4, 8, 12, etc.) - but only if not already active
      const shouldActivateXRay = newConsecutive % 4 === 0 && !xRayVision;
      
      if (shouldActivateXRay) {
        gameSounds.playXRayVision();
      }
      
      // Match Point fanfare - one away from advancing!
      if (newScore === 9) {
        gameSounds.playStreakBonus();
      }
      
      // Check if deck is complete
      if (newScore >= POINTS_TO_ADVANCE) {
        gameSounds.playMilestone();
        setTimeout(() => {
          const newDecksCompleted = decksCompleted + 1;
          setDecksCompleted(newDecksCompleted);
          setCelebration(getCelebration(newDecksCompleted));
          
          // Set next deck info (if not last)
          const nextIndex = currentDeckIndex + 1;
          if (nextIndex < availableDecks.length) {
            setNextDeckInfo(DECK_INFO[availableDecks[nextIndex]]);
          } else {
            setNextDeckInfo(null);
          }
          
          // Handle X-Ray for next deck - carry over if earned on this turn OR if currently active and unused
          if (shouldActivateXRay || xRayVision) {
            setPendingXRay(true);
          } else {
            setPendingXRay(false);
          }
          setXRayVision(false);
          
          setIsRevealing(false);
          setLastChoice(null);
          setAnimatingCard(null);
          setCardResult(null);
          setGameState(JOURNEY_STATES.DECK_COMPLETE);
        }, 1000);
      } else {
        // Continue with new cards - fast timing (1000ms)
        setTimeout(() => {
          setAnimatingCard(null);
          setCardResult(null);
          
          // Handle X-Ray Vision
          if (shouldActivateXRay) {
            setXRayVision(true);
          } else if (xRayVision) {
            // Turn off X-Ray Vision after it was used once
            setXRayVision(false);
          }
          
          setCurrentCards(getRandomCards(currentDeck));
          setIsRevealing(false);
          setLastChoice(null);
        }, 1000);
      }
    } else {
      // Wrong answer - slower timing (1500ms)
      gameSounds.playWrong();
      const newStrikes = strikes + 1;
      setStrikes(newStrikes);
      setConsecutiveCorrect(0);
      
      if (xRayVision) {
        setXRayVision(false);
      }
      setPendingXRay(false);
      
      if (newStrikes >= MAX_STRIKES) {
        gameSounds.playGameOver();
        setTimeout(() => {
          setAnimatingCard(null);
          setCardResult(null);
          setIsRevealing(false);
          setLastChoice(null);
          setGameState(JOURNEY_STATES.GAME_OVER);
        }, 1500);
      } else {
        setTimeout(() => {
          setAnimatingCard(null);
          setCardResult(null);
          setCurrentCards(getRandomCards(currentDeck));
          setIsRevealing(false);
          setLastChoice(null);
        }, 1500);
      }
    }
  }, [isRevealing, score, strikes, decksCompleted, currentDeck, currentDeckIndex, availableDecks, getRandomCards, consecutiveCorrect, xRayVision]);

  // Submit journey score
  const submitScore = useCallback(async () => {
    if (!user || scoreSubmitted) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const sessionToken = localStorage.getItem('session_token');
      
      const response = await fetch(`${backendUrl}/api/journey/scores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
        },
        credentials: 'include',
        body: JSON.stringify({
          decks_completed: decksCompleted,
          is_journey_complete: gameState === JOURNEY_STATES.JOURNEY_COMPLETE
        })
      });
      
      if (response.ok) {
        setScoreSubmitted(true);
      }
    } catch (error) {
      console.error('Failed to submit journey score:', error);
    }
  }, [user, scoreSubmitted, decksCompleted, gameState]);

  // Auto-submit score on game over or journey complete
  useEffect(() => {
    if ((gameState === JOURNEY_STATES.GAME_OVER || gameState === JOURNEY_STATES.JOURNEY_COMPLETE) && !scoreSubmitted && user) {
      submitScore();
    }
  }, [gameState, scoreSubmitted, user, submitScore]);

  // Get current deck info
  const deckInfo = currentDeck ? DECK_INFO[currentDeck] : null;
  const totalDecks = Object.keys(DECK_INFO).length;
  const progress = Math.round((decksCompleted / totalDecks) * 100);

  return {
    gameState,
    deckInfo,
    currentCards,
    score,
    strikes,
    maxStrikes: MAX_STRIKES,
    pointsToAdvance: POINTS_TO_ADVANCE,
    decksCompleted,
    totalDecks,
    progress,
    isRevealing,
    lastChoice,
    celebration,
    nextDeckInfo,
    scoreSubmitted,
    consecutiveCorrect,
    xRayVision,
    animatingCard,
    cardResult,
    startJourney,
    handleChoice,
    advanceToNextDeck,
    restartJourney: startJourney
  };
};
